// src/pages/projects/projectBoqItemsService.js
//
// PROJECT BOQ ITEMS — "Full Scope" reference view (Task 4.5)
//
// GOVERNANCE:
//   - CACHE/REFERENCE ONLY — this collection is a read-reference view of
//     the BOQ as originally specified (full description, brand/make,
//     original section/part structure). It is NEVER the source of truth
//     for procurement/budget tracking — project_material_stages remains
//     the sole engine for that (Constitution §1 Source-of-Truth Map).
//     Nothing here is ever read by MRF, GRN, Costing Card, or any
//     financial flow.
//   - Populated from the SAME single AI BOQ Import upload that also
//     creates project_material_stages — no second/parallel upload path.
//   - APPEND-ONLY: re-running AI BOQ Import (e.g. importing the
//     remaining rows of a large file next month after a quota stop)
//     adds MORE reference items, never deletes/overwrites existing ones
//     — consistent with the app's ledger-first "never silently lose
//     data" discipline. An explicit "clear scope" action, if ever
//     needed, would be its own separate, deliberate future feature.
//   - Flat per-item document collection (NOT an array field inside one
//     doc) — same pattern as material_requests/purchase_orders, chosen
//     over the material_budget[]-in-one-doc pattern used by
//     project_material_stages because full descriptions across an
//     800+-item BOQ risk the 1MB Firestore document-size limit if
//     nested inside a single doc.

import {
  collection, doc, writeBatch, getDocs, query, where, serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase";

const BATCH_LIMIT = 450; // stay safely under Firestore's 500-write-per-batch cap

// items: array of { stage, item_name, description, brand_make,
//   section_path, unit, qty, rate } — exactly the shape parseBoqWithAI.js
//   now returns per item. matched_item_id/is_new_item are Item-Master-
//   matching concerns (stages/procurement side) — irrelevant here and
//   intentionally not stored on this reference-only doc.
export const saveBoqItems = async (company_id, project_id, project_name, items, user) => {
  if (!company_id) throw new Error("company_id is required");
  if (!project_id) throw new Error("project_id is required");
  if (!Array.isArray(items) || items.length === 0) return { itemsSaved: 0 };

  let itemsSaved = 0;

  for (let i = 0; i < items.length; i += BATCH_LIMIT) {
    const batch = writeBatch(db);
    const slice = items.slice(i, i + BATCH_LIMIT);

    slice.forEach((item, sliceIdx) => {
      const ref = doc(collection(db, "project_boq_items"));
      batch.set(ref, {
        company_id,
        project_id,
        project_name:  project_name || "",
        stage:         item.stage         || "",
        item_name:     item.item_name     || "",
        description:   item.description   || "",
        brand_make:    item.brand_make    || "",
        section_path:  Array.isArray(item.section_path) ? item.section_path : [],
        unit:          item.unit          || "",
        qty:           Number(item.qty  || 0),
        rate:          Number(item.rate || 0),
        // Position within the confirmed items[] array at save time — this
        // IS the original BOQ's real sequence (extraction preserves source
        // row order; user edits change values, never row position). The
        // Full Scope tab's default table view sorts by this field so the
        // client sees the BOQ exactly the way they've always seen it —
        // top to bottom, in document order — not an AI-reorganized layout.
        // Older items saved before this field existed will lack it; the
        // UI treats a missing row_order as "sort last", never a crash.
        row_order:     i + sliceIdx,
        created_by:      user?.uid  || "",
        created_by_name: user?.name || user?.email || "",
        created_at:      serverTimestamp(),
      });
    });

    try {
      await batch.commit();
      itemsSaved += slice.length;
    } catch (err) {
      // A LATER batch failing must never hide how many items an EARLIER
      // batch already committed — attach the running count to the thrown
      // error so the caller (AIBoqImportPanel.jsx) can report a genuine
      // partial-success state to the Manager instead of treating this as
      // a total, silent loss.
      err.itemsSaved = itemsSaved;
      throw err;
    }
  }

  return { itemsSaved };
};

export const fetchBoqItems = async (company_id, project_id) => {
  const snap = await getDocs(query(
    collection(db, "project_boq_items"),
    where("company_id", "==", company_id),
    where("project_id", "==", project_id)
  ));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};